import { computeExposure, formatCents, type ExposureLot } from '@auctisync/shared';
import { useState } from 'react';

export type { ExposureLot };

/**
 * What this bidder is on the hook for, if the sale ended now.
 *
 * OPT-IN, DEFAULT OFF (`profiles.show_exposure_meter`, migration
 * 20260701180000). Some bidders want the running total in front of them; for
 * others it is the number that makes them stop bidding, and the house does not
 * get to decide which kind of bidder someone is.
 *
 * 🔴 EVERY FIGURE IS THIS BIDDER'S OWN. The ceilings come from their own `bids`
 * rows under `bids_self_select`; no other bidder's maximum is readable here, or
 * anywhere on a client. Showing a rival's ceiling would hand away the one thing
 * proxy bidding exists to keep secret.
 *
 * ALL-IN IS THE DEFAULT VIEW, because the hammer price is not what anyone pays.
 * The toggle to hammer-only is there for reconciling against the room, not
 * because it is the more honest number.
 *
 * REHOMED from the live room's header into the right-hand column (2026-09-03,
 * web design handoff). It was a chip with a popover; the column it now lives in
 * is exactly "where you stand", so it can simply be open.
 */
export function ExposureMeter({
  winningLots,
  userMax,
  premiumBps,
}: {
  winningLots: readonly ExposureLot[];
  /** Lot id → this bidder's own ceiling, in cents. */
  userMax: ReadonlyMap<string, number>;
  premiumBps: number | null;
}) {
  const [allIn, setAllIn] = useState(true);
  const [open, setOpen] = useState(false);

  /*
   * 🔴 THE SUMS MOVED INTO `@auctisync/shared` ON 2026-09-22, when the phone
   * got a meter of its own. They are arithmetic about somebody's money, and a
   * second copy is what `feedback_never_retype_a_money_function` exists to
   * stop. Nothing about the numbers changed — this is the same function with
   * one caller more.
   */
  const {
    winningNow,
    maxExposure,
    perLot,
    premiumLabel: pct,
  } = computeExposure(winningLots, userMax, premiumBps, allIn);

  return (
    <div className="wl-panel">
      <div className="wl-ph">
        <span className="w-lbl">If the sale ended now</span>
        <button
          type="button"
          className="muted"
          style={{ fontSize: 11, textDecoration: 'underline' }}
          onClick={() => setAllIn((v) => !v)}
        >
          {allIn ? `incl. ${pct}% premium` : 'hammer only'}
        </button>
      </div>
      <div style={{ padding: '4px 14px' }}>
        <div className="w-kv" style={{ fontSize: 13.5 }}>
          <span className="muted">Winning now</span>
          <b className="mono">{formatCents(winningNow)}</b>
        </div>
        <div className="w-kv" style={{ fontSize: 13.5 }}>
          <span className="muted">If every maximum is reached</span>
          <b className="mono">{formatCents(maxExposure)}</b>
        </div>
      </div>
      {winningLots.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5, padding: '0 14px 14px', margin: 0 }}>
          You are not leading any lot in this sale.
        </p>
      ) : (
        <>
          <button
            type="button"
            className="muted"
            style={{ fontSize: 12.5, padding: '0 14px 12px', textDecoration: 'underline' }}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? 'Hide the lots' : `Show the ${String(winningLots.length)} lots`}
          </button>
          {open ? (
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {winningLots.map((l) => (
                <div className="wl-ev" key={l.id}>
                  <span className="muted">Lot {l.lotNumber}</span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {l.title}
                  </span>
                  <b>{formatCents(perLot.get(l.id) ?? 0)}</b>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
